import SubstanceListVisibilityRuleEvaluator from './SubstanceListVisibilityRuleEvaluator'

function uniqueIds(ids) {
    return Array.from(new Set(
        ids
            .map(id => Number(id))
            .filter(id => Number.isFinite(id)),
    ))
}

function hasLegislationIntersection(list, legislations) {
    if (legislations.length === 0) {
        return true
    }

    return list.hasIntersectionWithLegislations(legislations)
}

function getReferenceListId(reference) {
    return Number(reference.listId)
}

function buildIdToListMap(lists) {
    return lists.reduce((map, list) => ({
        ...map,
        [list.getId()]: list,
    }), {})
}

export default class SubstanceListReferenceGraph {
    constructor(lists = [], options = {}) {
        this.lists = lists
        this.evaluator = options.evaluator || new SubstanceListVisibilityRuleEvaluator(options)
        this.idToList = buildIdToListMap(lists)
    }

    resolveActiveLists(legislations = [], productContext = {}) {
        const activeIds = []
        const visit = list => {
            if (!list || activeIds.includes(list.getId())) {
                return
            }

            if (!this.isListVisible(list, productContext)) {
                return
            }

            activeIds.push(list.getId())

            this.getVisibleReferences(list, productContext)
                .forEach(reference => visit(this.idToList[getReferenceListId(reference)]))
        }

        this.lists
            .filter(list => list.key !== 'empty')
            .filter(list => hasLegislationIntersection(list, legislations))
            .forEach(visit)

        return activeIds
            .map(id => this.idToList[id])
            .filter(Boolean)
    }

    resolveRootLists(legislations = [], productContext = {}) {
        return this.lists
            .filter(list => list.key !== 'empty')
            .filter(list => hasLegislationIntersection(list, legislations))
            .filter(list => this.isListVisible(list, productContext))
    }

    getListById(listId) {
        return this.idToList[Number(listId)] || null
    }

    buildReferencedToParentsMap(sourceLists, productContext = {}) {
        const map = {}

        sourceLists.forEach(parentList => {
            this.getVisibleReferences(parentList, productContext).forEach(reference => {
                const referencedList = this.getListById(getReferenceListId(reference))

                if (!referencedList) {
                    return
                }

                if (!sourceLists.find(list => list.getId() === referencedList.getId())) {
                    return
                }

                if (!map[referencedList.id]) {
                    map[referencedList.id] = []
                }

                map[referencedList.id].push(parentList)
            })
        })

        return map
    }

    buildReferencedToParentPathsMap(rootLists, sourceLists, productContext = {}) {
        const sourceListIds = sourceLists.map(list => list.getId())
        const map = {}
        const pathKeys = {}
        const visit = (parentList, parentPath, visitedIds) => {
            this.getVisibleReferences(parentList, productContext).forEach(reference => {
                const referencedList = this.idToList[getReferenceListId(reference)]

                if (!referencedList || !sourceListIds.includes(referencedList.getId())) {
                    return
                }

                if (visitedIds.includes(referencedList.getId())) {
                    return
                }

                if (!this.isListVisible(referencedList, productContext)) {
                    return
                }

                const nextParentPath = parentPath.concat([parentList])
                const pathKey = nextParentPath.map(list => list.getId()).join('>')

                if (!map[referencedList.id]) {
                    map[referencedList.id] = []
                }

                if (!pathKeys[referencedList.id]) {
                    pathKeys[referencedList.id] = []
                }

                if (pathKeys[referencedList.id].includes(pathKey)) {
                    return
                }

                pathKeys[referencedList.id].push(pathKey)
                map[referencedList.id].push(nextParentPath)
                visit(referencedList, nextParentPath, visitedIds.concat([referencedList.getId()]))
            })
        }

        rootLists.forEach(list => visit(list, [], [list.getId()]))

        return map
    }

    getTransitiveReferencedListIds(list, productContext = {}) {
        const referencedIds = []
        const visitedIds = [list.getId()]
        const visit = parentList => {
            this.getVisibleReferences(parentList, productContext).forEach(reference => {
                const listId = getReferenceListId(reference)
                const referencedList = this.idToList[listId]

                if (!referencedList || visitedIds.includes(listId)) {
                    return
                }

                if (!this.isListVisible(referencedList, productContext)) {
                    return
                }

                visitedIds.push(listId)
                referencedIds.push(listId)
                visit(referencedList)
            })
        }

        visit(list)

        return referencedIds
    }

    getVisibleReferences(list, productContext = {}) {
        return list
            .getReferences()
            .filter(reference => this.evaluator.matches(reference.visibility, productContext))
    }

    isListVisible(list, productContext = {}) {
        return this.evaluator.matches(list.getVisibilityRule(), productContext)
    }

    getVisibleReferencedListIds(list, productContext = {}) {
        return uniqueIds(this
            .getVisibleReferences(list, productContext)
            .map(getReferenceListId))
    }
}
